import { ModalController } from "@theme/modal-controller";

// Shared fetch — runs once regardless of how many <localization-form> instances exist on the page
let _locationPromise = null;
function getLocationSuggestion() {
  if (!_locationPromise) {
    _locationPromise = fetch(
      window.Shopify.routes.root +
        "browsing_context_suggestions.json" +
        "?country[enabled]=true" +
        `&country[exclude]=${window.Shopify.country}` +
        "&language[enabled]=true" +
        `&language[exclude]=${window.Shopify.language}`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return _locationPromise;
}

function getBrowserCountryFallback() {
  const lang = navigator.language || "";
  const parts = lang.split("-");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : null;
}

// ── LocalizationForm ──────────────────────────────────────────────────────────

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector("button"),
      panel: this.querySelector("ul"),
    };
    this.elements.button.addEventListener("click", this.openSelector.bind(this));
    this.elements.button.addEventListener("focusout", this.closeSelector.bind(this));
    this.addEventListener("keyup", this.onContainerKeyUp.bind(this));
    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this)),
    );

    if (!sessionStorage.getItem("localization_form_submitted")) {
      this.detectLocation();
    }
  }

  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.elements.panel.setAttribute("hidden", true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector("form");
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) {
      form.submit();
      sessionStorage.setItem("localization_form_submitted", "true");
    }
  }

  openSelector() {
    this.elements.button.focus();
    this.elements.panel.toggleAttribute("hidden");
    this.elements.button.setAttribute(
      "aria-expanded",
      (this.elements.button.getAttribute("aria-expanded") === "false").toString(),
    );
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === "BUTTON";
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }

  async detectLocation() {
    try {
      const data = await getLocationSuggestion();
      if (!data?.suggestions?.length) return;

      const suggestion = data.suggestions[0];
      const currentCountry =
        window.Theme?.localization?.country?.iso_code || window.Shopify.country;

      if (
        suggestion.parts.country?.handle === currentCountry ||
        suggestion.parts.country?.name === window.Theme?.localization?.country?.name
      ) {
        return;
      }

      const modal = document.querySelector('[data-modal="location-selector-id"]');
      if (!modal) {
        console.warn("Location selector modal not found in DOM");
        return;
      }

      const modalController = new ModalController();
      modalController.presentModal("location-selector-id");
    } catch (error) {
      console.error("Error fetching browsing context suggestions:", error);
    }
  }
}

// ── LocaleNotice (store switcher) ────────────────────────────────────────────

const LN_KEY = "_qf_ln";
const LN_PARAM = "_qfln";

// Known two-part ccTLDs — prevents setting a cookie on a public suffix (e.g. ".co.uk")
const MULTI_PART_TLDS = new Set([
  "co.uk", "co.nz", "co.za", "co.jp", "co.in", "co.kr",
  "com.au", "com.br", "com.mx", "com.ar", "com.sg", "com.hk",
  "net.au", "org.au", "gov.au", "edu.au", "org.uk", "me.uk", "ne.jp",
]);

function cookieParentDomain(hostname) {
  const parts = hostname.split(".");
  if (parts.length <= 2) return hostname;
  const tldTwo = parts.slice(-2).join(".");
  const regDomain = MULTI_PART_TLDS.has(tldTwo) ? parts.slice(-3).join(".") : parts.slice(-2).join(".");
  return hostname === regDomain ? hostname : "." + regDomain;
}

function setNoticeDismissed() {
  document.cookie = `${LN_KEY}=1; Domain=${cookieParentDomain(location.hostname)}; Path=/; Max-Age=2592000; SameSite=Lax`;
  try {
    localStorage.setItem(LN_KEY, "1");
  } catch (_) {}
}

function isNoticeDismissed() {
  const params = new URLSearchParams(location.search);
  if (params.get(LN_PARAM)) {
    setNoticeDismissed();
    params.delete(LN_PARAM);
    const cleaned =
      location.pathname + (params.toString() ? "?" + params.toString() : "") + location.hash;
    history.replaceState(null, "", cleaned);
    return true;
  }
  if (document.cookie.split(";").some((c) => c.trim().startsWith(LN_KEY + "="))) return true;
  try {
    return !!localStorage.getItem(LN_KEY);
  } catch (_) {
    return false;
  }
}

class LocaleNotice extends HTMLElement {
  connectedCallback() {
    if (isNoticeDismissed()) return;

    let stores = [];
    try {
      stores = JSON.parse(this.dataset.stores || "[]");
    } catch (e) {
      return;
    }
    if (!stores.length) return;

    this.detectAndShow(stores);
  }

  async detectAndShow(stores) {
    try {
      let detectedCountry = null;

      const data = await getLocationSuggestion();
      if (data?.suggestions?.length) {
        detectedCountry = data.suggestions[0].parts.country?.handle?.toUpperCase();
      }
      if (!detectedCountry) detectedCountry = getBrowserCountryFallback();
      if (!detectedCountry) return;

      const match = stores.find(
        (s) =>
          Array.isArray(s.countries) &&
          s.countries.map((c) => c.toUpperCase()).includes(detectedCountry),
      );
      if (!match) return;

      try {
        if (new URL(match.url, location.origin).origin === location.origin) return;
      } catch (_) {}

      const message = this.querySelector("#localeMessage");
      const link = this.querySelector("#localeLink");
      const dismiss = this.querySelector("#localeDismiss");

      if (message) {
        message.textContent = (message.dataset.template || "").replace("##", match.label);
      }
      if (link) {
        const dest = new URL(match.url, location.origin);
        const discount = new URLSearchParams(location.search).get("discount");
        if (discount && !dest.searchParams.has("discount")) dest.searchParams.set("discount", discount);
        dest.searchParams.set(LN_PARAM, "1");
        link.href = dest.toString();
      }
      if (dismiss) {
        dismiss.addEventListener("click", () => {
          setNoticeDismissed();
          this.hidden = true;
        });
      }

      this.removeAttribute("hidden");
    } catch (e) {
      // Enhancement only — fail silently
    }
  }
}

if (!customElements.get("localization-form")) {
  customElements.define("localization-form", LocalizationForm);
}
if (!customElements.get("locale-notice")) {
  customElements.define("locale-notice", LocaleNotice);
}